import mixpanel from "mixpanel-browser";
import { DEFAULT_APP_VERSION } from "./constants";
import {
  getProductDetailBasedonProduct,
  includesInString,
  convertToHrs,
} from "./jsUtils";
import Cookies from "universal-cookie";
import { getNotifications } from "./actions";
import { store } from "../helpers";
import { ASSET_TYPES } from "../constants";
import toolsInfo from "./toolsInfo";

// FIXED: Initialize with proper state tracking
let mixpanelObj = null;
let isInitialized = false;
let initializationError = null;

const cookies = new Cookies();

const getTokenEnv = () => {
  const config = window.RUNTIME_MIXPANEL_CONFIG;

  return config && config.enabled ? config : null;
};

const isTestEmail = (email) =>
  includesInString(email || "", "@yopmail.com") ||
  includesInString(email || "", "@fluke.com");

// NEW: Create mock Mixpanel for error scenarios
const createMockMixpanel = () => ({
  track: (eventName, properties) => {
    console.log('Mixpanel Mock - Track:', eventName, properties);
  },
  identify: (userId) => {
    console.log('Mixpanel Mock - Identify:', userId);
  },
  people: {
    set: (properties) => {
      console.log('Mixpanel Mock - People Set:', properties);
    },
    increment: (property, value) => {
      console.log('Mixpanel Mock - People Increment:', property, value);
    }
  },
  register: (properties) => {
    console.log('Mixpanel Mock - Register:', properties);
  },
  time_event: (eventName) => {
    console.log('Mixpanel Mock - Time Event:', eventName);
  },
  alias: (userId) => {
    console.log('Mixpanel Mock - Alias:', userId);
  }
});

// NEW: Safe execution wrapper
const safeExecute = (fn, fallback = () => {}) => {
  try {
    const result = fn();
    return result && typeof result.catch === "function"
      ? result.catch((error) => {
          console.error('Mixpanel operation error:', error);
          return fallback();
        })
      : result;
  } catch (error) {
    console.error('Mixpanel operation error:', error);
    return fallback();
  }
};

// FIXED: Enhanced initialization with error handling
const initiateMixpanel = (pageName, isV2) => {
  try {
    let userObj = store.getState("user").login.user;
    let user = getUser(userObj);
    
    // Enhanced user validation
    if (!userObj || !user || !user.productId || !user.emailAddr) {
      console.warn('Mixpanel: Invalid user data, skipping initialization');
      isInitialized = true;
      return createMockMixpanel();
    }

    let mixpanelVersion;
    let appVersion = getCurrentAppVersion();
    var productDetails = getProductDetailBasedonProduct(user.productId);

    // Token validation
    const TOKEN_ENV = getTokenEnv();
    if (!TOKEN_ENV || (!TOKEN_ENV.token && !TOKEN_ENV.token_v2)) {
      console.warn('Mixpanel: No tokens available');
      isInitialized = true;
      return createMockMixpanel();
    }

    const TOKEN = isV2 ? TOKEN_ENV["token_v2"] : TOKEN_ENV["token"];
    
    if (!TOKEN) {
      console.warn(`Mixpanel: No ${isV2 ? 'v2' : 'v1'} token available`);
      isInitialized = true;
      return createMockMixpanel();
    }

    // Initialize with proper configuration
    if (isV2) {
      mixpanel.init(TOKEN, {
        debug: false,
        disable_all_events: false, // Explicitly set this property
        track_pageview: true,
        persistence: 'localStorage'
      }, "mpv2");
      mixpanelVersion = mixpanel.mpv2;
    } else {
      mixpanel.init(TOKEN, {
        debug: false,
        disable_all_events: false, // Explicitly set this property
        track_pageview: true,
        persistence: 'localStorage'
      });
      mixpanelVersion = mixpanel;
    }

    // Register user properties with validation
    const registrationData = {
      $email: user.emailAddr.toLowerCase(),
      $first_name: user.firstName || 'Unknown',
      $last_name: user.lastName || 'Unknown',
      "Subscription Type": productDetails && productDetails.productType 
        ? productDetails.productType 
        : "Free",
      "License Type": productDetails && productDetails.productName 
        ? productDetails.productName 
        : "FC Assets - Trial",
      "Page Name": pageName || 'Unknown',
      "User ID": user.userAccountId,
      "Product ID": "FC Webapp",
      "App Version": appVersion,
    };

    mixpanelVersion.register(registrationData);
    mixpanelObj = mixpanelVersion;
    isInitialized = true;
    initializationError = null;
    
    console.log('Mixpanel initialized successfully');
    return mixpanelVersion;
    
  } catch (error) {
    console.error('Mixpanel initialization error:', error);
    initializationError = error;
    isInitialized = true;
    return createMockMixpanel();
  }
};

const getCurrentAppVersion = () => {
  return window.RUNTIME_APP_VERSION || cookies.get("appVersion") || DEFAULT_APP_VERSION;
};

export async function getDeviceInfo() {
  const userAgent = navigator.userAgent;
  const userAgentData = navigator.userAgentData;
  const isIPhone = /iPhone|CriOS|FxiOS/i.test(userAgent);
  const isIPad = /iPad/i.test(userAgent);
  const isIPadOS = !isIPad && !isIPhone && (
    (userAgentData?.platform === "macOS" && userAgentData.mobile) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
  const type = isIPad || isIPadOS
    ? "tablet"
    : userAgentData?.mobile || isIPhone
      ? "mobile"
      : "desktop";
  const isIOS = isIPhone || isIPad || isIPadOS;
  let os = "Unknown";
  let osVersion = "Unknown";

  const windowsMatch = userAgent.match(/Windows NT ([\d.]+)/i);
  const androidMatch = userAgent.match(/Android ([\d.]+)/i);
  const iosMatch = userAgent.match(/(?:CPU (?:iPhone )?OS|iPhone OS) ([\d_]+)/i);
  const macMatch = userAgent.match(/Mac OS X ([\d_]+)/i);

  if (windowsMatch) {
    os = "Windows";
    osVersion = windowsMatch[1];
  } else if (androidMatch) {
    os = "Android";
    osVersion = androidMatch[1];
  } else if (isIOS) {
    os = isIPadOS ? "iPadOS" : "iOS";
    if (iosMatch) osVersion = iosMatch[1].replace(/_/g, ".");
  } else if (/Macintosh|Mac OS X/i.test(userAgent)) {
    os = "macOS";
    if (macMatch) osVersion = macMatch[1].replace(/_/g, ".");
  } else if (/Linux/i.test(userAgent)) {
    os = "Linux";
  }

  let model = "Unknown";
  if (userAgentData?.getHighEntropyValues) {
    try {
      const data = await userAgentData.getHighEntropyValues([
        "model",
        "platformVersion",
      ]);
      if (data.model) model = data.model;
      if (typeof data.platformVersion === "string" && data.platformVersion && os === "macOS") {
        osVersion = data.platformVersion;
      }
    } catch (error) {
      console.error('Error fetching device model:', error);
    }
  }

  const browserPatterns = [
    { name: "Edge", regex: /Edg\/([\d.]+)/i },
    { name: "Opera", regex: /(?:OPR|Opera)\/([\d.]+)/i },
    { name: "Chrome", regex: /CriOS\/([\d.]+)/i },
    { name: "Firefox", regex: /FxiOS\/([\d.]+)/i },
    { name: "Firefox", regex: /Firefox\/([\d.]+)/i },
    { name: "Chrome", regex: /Chrome\/([\d.]+)/i },
    { name: "Safari", regex: /Version\/([\d.]+).*Safari/i },
  ];
  let browser = "Unknown";
  let browserVersion = "Unknown";
  for (const { name, regex } of browserPatterns) {
    const match = userAgent.match(regex);
    if (match) {
      browser = name;
      browserVersion = match[1];
      break;
    }
  }

  if (model === "Unknown") {
    model = isIOS ? "iPhone/iPad" : os === "macOS" ? "Mac" : os === "Windows" ? "Windows PC" : "Unknown";
  }

  const brand = isIOS || os === "macOS"
    ? "Apple"
    : "Windows";

  return {
    type,
    brand,
    model,
    os,
    osVersion,
    browser,
    browserVersion,
  };
}

// Auth events fire before initiateMixpanel() may have run, so they lazily
// init their own instance rather than depending on the module-level one.
const getAuthMixpanelInstance = (user) => {
  if (mixpanelObj) {
    if (user?.userAccountId && typeof mixpanelObj.identify === "function") {
      mixpanelObj.identify(user.userAccountId);
    }
    return mixpanelObj;
  }

  const TOKEN_ENV = getTokenEnv();
  const token = TOKEN_ENV?.token;
  if (!token) return null;

  mixpanel.init(token, { persistence: "localStorage" });
  mixpanelObj = mixpanel;

  if (user?.userAccountId) mixpanelObj.identify(user.userAccountId);
  return mixpanelObj;
};

const trackAuthenticationEvent = async ({
  eventName,
  status,
  startedAt,
  email,
  user,
}) => {
  return safeExecute(async () => {
    if (isTestEmail(user?.emailAddr || email)) return;

    const instance = getAuthMixpanelInstance(user);
    if (!instance) return;

    const browserDetails = await getDeviceInfo();
    const duration = `${((Date.now() - startedAt) / 1000).toFixed(3)}s`;
    const superProperties = {
      Email: (user?.emailAddr || email || "").toLowerCase(),
      "Product ID": "FC WebApp",
      "App Version": getCurrentAppVersion(),
      Brand: browserDetails.brand,
      "OS Version": browserDetails.osVersion,
      Browser: browserDetails.browser,
      "Browser Version": browserDetails.browserVersion,
      Model: browserDetails.model,
      Duration: duration,
      "First Name": user?.firstName || "",
      "Last Name": user?.lastName || "",
      "User ID": user?.userAccountId || "",
    };

    if (typeof instance.register === "function") instance.register(superProperties);
    instance.track(eventName, {
      ...superProperties,
      Status: status === "Successful" ? "Successful" : "Failed",
    });
  });
};

// FIXED: Enhanced with error handling
const viewSessionDetail = (session, mixpanelInstance) => {
  safeExecute(() => {
    let userObj = store.getState("user").login.user;
    let user = getUser(userObj);
    
    if (!user || !user.emailAddr) {
      console.warn('Mixpanel: Invalid user for session tracking');
      return;
    }
    
    if (
      user &&
      !includesInString(user.emailAddr, "@yopmail.com") &&
      !includesInString(user.emailAddr, "@fluke.com")
    ) {
      let params = { sessionId: session.sessionId, emailId: user.emailAddr };
      getNotifications(params).then(
        (response) => {
          triggerMixPanelEvent(response.detailNotificationCount, session, mixpanelInstance);
        },
        (reject) => {
          triggerMixPanelEvent(null, session, mixpanelInstance);
        }
      );
    }
  });
};

// FIXED: Safe cookie operations
const setMixpanelFlagsViewSession = (property) => {
  safeExecute(() => {
    if (cookies.get("session_details_object_mixpanel")) {
      let sessionDetailsObj = cookies.get("session_details_object_mixpanel");
      sessionDetailsObj[property] = true;
      cookies.set("session_details_object_mixpanel", sessionDetailsObj);
    }
  });
};

const triggerMixPanelEvent = (notificationObj, session, mixpanelInstance) => {
  safeExecute(() => {
    let sessionDetailsObj = cookies.get("session_details_object_mixpanel");
    if (sessionDetailsObj && mixpanelInstance) {
      viewSession(session, mixpanelInstance);
      if (
        sessionDetailsObj.sensorType &&
        sessionDetailsObj.sensorType === "Vibration"
      ) {
        viewVibrationSession(notificationObj, session, mixpanelInstance);
      }
    }
  });
};

const getSensorTypesList = (sensorType, noOfSensors) => {
  let sensorTypes = [];
  for (let i = 0; i < noOfSensors; i++) sensorTypes.push(sensorType);
  return sensorTypes;
};

function getAssetTypes(session) {
  if (!session || !session.sensors) return ['Other'];
  
  return session.sensors.map((item) => {
    const matchedAssetType = ASSET_TYPES.find(
      (assetType) =>
        assetType.asset_type_id.toUpperCase() === item.assetTypeId.toUpperCase()
    );
    return matchedAssetType ? matchedAssetType.admin_desc : "Other";
  });
}

const viewSession = (session, mixpanelInstance) => {
  safeExecute(() => {
    if (!mixpanelInstance || !session) return;
    
    let sessionDetailsObj = cookies.get("session_details_object_mixpanel");
    if (!sessionDetailsObj) return;
    
    mixpanelInstance.track("View Session", {
      "Sensor Types": session.sensors ? session.sensors.map(() => sessionDetailsObj.sensorType) : [],
      "Number of Sensors": sessionDetailsObj.noOfSensors || 0,
      "Asset Type": getAssetTypes(session),
    });
  });
};

const viewVibrationSession = (notificationObj, session, mixpanelInstance) => {
  safeExecute(() => {
    if (!mixpanelInstance || !session) return;
    
    let userObj = store.getState("user").login.user;
    let user = getUser(userObj);
    let sessionDetailsObj = cookies.get("session_details_object_mixpanel");
    let people = cookies.get("people") || {};
    
    if (!user || !sessionDetailsObj) return;
    
    people["Number of 3561FC Sensors"] = sessionDetailsObj.noOfSensors || 0;
    mixpanelInstance.people.set(people);
    
    mixpanelInstance.track("View Vibration Session", {
      "Sensor Types": getSensorTypesList(
        sessionDetailsObj.sensorType,
        sessionDetailsObj.noOfSensors || 0
      ),
      "Number of Sensors": sessionDetailsObj.noOfSensors || 0,
      "Asset Type": getAssetTypes(session),
      "Session Duration":
        sessionDetailsObj.sessionEndTime > 0
          ? convertToHrs(
              sessionDetailsObj.sessionStartTime,
              sessionDetailsObj.sessionEndTime
            )
          : convertToHrs(sessionDetailsObj.sessionStartTime, Date.now()),
      "Gateway Disconnection Count": notificationObj
        ? notificationObj["gatewayDisconnectionCount"] || 0
        : 0,
      "Gateway Reconnection Count": notificationObj
        ? notificationObj["gatewayReconnectionCount"] || 0
        : 0,
      "Sensor Disconnection Count": notificationObj
        ? notificationObj["sensorDisconnectionCount"] || 0
        : 0,
      "Sensor Reconnection Count": notificationObj
        ? notificationObj["sensorReconnectionCount"] || 0
        : 0,
      "Number of Alarms": sessionDetailsObj.noOfAlarms || 0,
      "Number of Alarms marked as read": notificationObj
        ? notificationObj["alarmsMarkedAsReadCount"] || 0
        : 0,
      "Added Sensors": 0,
      "Add Sensor Errors": 0,
      "Add Sensor Error Names": [],
      "Removed Sensors": 0,
      "Remove Sensor Errors": 0,
      "Remove Sensor Error Names": [],
      "Reconfigured WiFi": false,
      "Reconfiguring WiFi Error Count": 0,
      "Reconfiguring WiFi Error Names": [],
      "Changed Vibration Unit": sessionDetailsObj.changedVibrationUnit || false,
      "Changed Temperature Unit": sessionDetailsObj.changedTemperatureUnit || false,
      "Scrolled Graph": sessionDetailsObj.scrolledGraph || false,
      "Zoomed Graph": sessionDetailsObj.zoomedGraph || false,
      "Viewed Alarms for an Asset": sessionDetailsObj.viewdAlarms || false,
    });
    
    mixpanelInstance.identify(user.userAccountId);
    cookies.remove("session_details_object_mixpanel");
  });
};

const setupAlarms = (alarmObj, assetTypeId, mixpanelInstance) => {
  safeExecute(() => {
    if (!mixpanelInstance || !alarmObj) return;
    
    const matchedAssetType = ASSET_TYPES.find(
      (assetType) => assetType.asset_type_id.toUpperCase() === assetTypeId.toUpperCase()
    );
    
    alarmObj["Asset Type"] = [matchedAssetType ? matchedAssetType.admin_desc : "Other"];
    let userObj = store.getState("user").login.user;
    let user = getUser(userObj);
    let people = cookies.get("people") || {};
    
    if (!user || isTestEmail(user.emailAddr)) return;
    
    mixpanelInstance.track("Setup Alarms", alarmObj);
    mixpanelInstance.identify(user.userAccountId);
    mixpanelInstance.people.set(people);
  });
};

const onClearAssignment = (measurementHeaders, addWOFlag, addEquipmentFlag) => {
  safeExecute(() => {
    if (!measurementHeaders) {
      console.warn('Mixpanel: No measurement headers provided');
      return;
    }
    
    let toolDetails = getMixPanelDataFromMeasurementHeader(measurementHeaders);
    postMixpanelData(
      toolDetails.toolName,
      toolDetails.toolCategory,
      toolDetails.toolType,
      false,
      addWOFlag,
      addEquipmentFlag
    );
  });
};

// FIXED: Safe measurement notes update tracking
const onUpdateMeasNotes = (measurementHeaders) => {
  safeExecute(() => {
    if (!measurementHeaders) {
      console.warn('Mixpanel: No measurement headers provided');
      return;
    }
    
    let toolDetails = getMixPanelDataFromMeasurementHeader(measurementHeaders);
    postMixpanelData(
      toolDetails.toolName,
      toolDetails.toolCategory,
      toolDetails.toolType,
      true,
      false,
      false
    );
  });
};

// FIXED: Main issue - use returned mixpanel instance and add error handling
const postMixpanelData = (
  toolName,
  toolCategory,
  toolType,
  addNoteFlag,
  addWOFlag,
  addEquipmentFlag
) => {
  safeExecute(() => {
    let userObj = store.getState("user").login.user;
    let user = getUser(userObj);
    
    if (!user || !user.userAccountId) {
      console.warn('Mixpanel: Invalid user for tracking');
      return;
    }
    if (isTestEmail(user.emailAddr)) return;
    
    let currentVersionInfo = getCurrentAppVersion();
    
    // Use the already-initialized module-level instance.
    // initiateMixpanel is async — calling it here would return a Promise, not an instance.
    const mixpanelInstance = mixpanelObj;
    
    if (!mixpanelInstance) {
      console.warn('Mixpanel: Not yet initialized, skipping track');
      return;
    }
    
    // Use the proper instance instead of global mixpanel
    mixpanelInstance.track("Modify Measurement", {
      "Tool Name": toolName,
      "Tool Category": toolCategory,
      "Tool Type": toolType,
      "Successfully Saved": true,
      "Added Notes": addNoteFlag,
      "Added Work Order": addWOFlag,
      "Added Equipment": addEquipmentFlag,
      "Save Error": "No",
      $app_version_string: currentVersionInfo,
    });
    
    if (mixpanelInstance.people && typeof mixpanelInstance.people.increment === 'function') {
      mixpanelInstance.people.increment("# of Modified Measurements", 1);
    }
    
    mixpanelInstance.identify(user.userAccountId);
  });
};

const getMixPanelDataFromMeasurementHeader = (measurementHeaders) => {
  let resObj = {
    toolCategory: [],
    toolType: [],
    toolName: [],
  };
  
  if (!measurementHeaders || !Array.isArray(measurementHeaders)) {
    return {
      toolCategory: "Unknown",
      toolType: "Unknown", 
      toolName: "Unknown"
    };
  }
  
  let matchedTool = {};
  let name;
  
  measurementHeaders.forEach((measurementHeader) => {
    if (!measurementHeader) return;
    
    name =
      measurementHeader.modelName == null
        ? measurementHeader.deviceType
        : measurementHeader.modelName;
    var modelCheck = name ? name.replace(/[^0-9]/g, "") : "";

    if (
      name == null ||
      name === "" ||
      name.toLowerCase().indexOf("automation") >= 0 ||
      modelCheck.length === 0
    ) {
      name = measurementHeader.deviceType;
    } else if (
      name != null &&
      name !== "" &&
      !(name.indexOf("Manual Entry") >= 0)
    ) {
      modelCheck = name.replace(/[^0-9]/g, "");
      if (modelCheck.length === 0) {
        name = measurementHeader.modelName;
      }
    }
    
    if (name && name.indexOf("Manual Entry") >= 0) {
      resObj["toolName"].push(name);
      resObj["toolCategory"].push("Manual Entry");
      resObj["toolType"].push("Manual Entry");
    } else if (name) {
      if (name.indexOf("-") >= 0) {
        name = name.slice(0, name.indexOf("-"));
      }
      if (name.indexOf("_") >= 0) {
        name = name.slice(0, name.indexOf("_"));
      }
      if (name.indexOf("FC") > 0) {
        name = name.slice(0, name.indexOf("FC") + 2);
      }

      var modelToSearch = name.replace(/[^0-9]/g, "");
      var matchedTools = [];

      // search by tool model number
      if (modelToSearch.length > 0) {
        matchedTools = filterToolsSearchForMixpanel(
          toolsInfo,
          modelToSearch,
          0
        );
      } else {
        matchedTools = filterToolsSearchForMixpanel(
          toolsInfo,
          name.toUpperCase(),
          0
        );
        // search by tool name
      }
      if (matchedTools.length === 1) {
        resObj["toolName"].push(matchedTools[0].DisplayName);
        resObj["toolCategory"].push(matchedTools[0].ToolCategory);
        resObj["toolType"].push(matchedTools[0].ToolType);
      } else if (matchedTools.length === 0) {
        // no match with model number
        matchedTool = searchToolByHardcodedNameForMixpanel(name);
        resObj["toolName"].push(matchedTool["toolName"]);
        resObj["toolCategory"].push(matchedTool["toolCategory"]);
        resObj["toolType"].push(matchedTool["toolType"]);
      } else if (matchedTools.length > 1) {
        var toolNameMatched = filterToolsSearchForMixpanel(
          matchedTools,
          name.toUpperCase(),
          1
        );

        if (toolNameMatched.length === 1) {
          // from tool name only one match.... correct tool name matched
          resObj["toolName"].push(matchedTools[0].DisplayName);
          resObj["toolCategory"].push(matchedTools[0].ToolCategory);
          resObj["toolType"].push(matchedTools[0].ToolType);
        } else if (toolNameMatched.length === 0) {
          // no match from tool name
          matchedTool = searchToolByHardcodedNameForMixpanel(name);
          resObj["toolName"].push(matchedTool["toolName"]);
          resObj["toolCategory"].push(matchedTool["toolCategory"]);
          resObj["toolType"].push(matchedTool["toolType"]);
        } else if (toolNameMatched.length > 1) {
          // more then one matches with tool name
          // if search is still not successful, search by tool name. back up if all cases failed.
          // search by tool hardcoded names
          matchedTool = searchToolByHardcodedNameForMixpanel(name);
          resObj["toolName"].push(matchedTool["toolName"]);
          resObj["toolCategory"].push(matchedTool["toolCategory"]);
          resObj["toolType"].push(matchedTool["toolType"]);
        }
      }
    } else {
      if (measurementHeader.deviceId) {
        // To feed toolType and toolName for Android devices
        matchedTool = searchToolByHardcodedNameForMixpanel(
          measurementHeader.deviceId
        );
        resObj["toolName"].push(matchedTool["toolName"]);
        resObj["toolCategory"].push(matchedTool["toolCategory"]);
        resObj["toolType"].push(matchedTool["toolType"]);
      }
    }
  });
  
  return resObj;
};

const searchToolByHardcodedNameForMixpanel = (toolName) => {
  if (!toolName) {
    return {
      toolCategory: "Unknown",
      toolType: "Unknown",
      toolName: "Unknown"
    };
  }
  
  var resObj = {
    toolCategory: "",
    toolType: "",
    toolName: toolName,
  };
  
  if (toolName.indexOf("1664") >= 0) {
    resObj["toolCategory"] = "Installation Testers";
    resObj["toolType"] = "Handheld";
  } else if (toolName.indexOf("TI") >= 0 || toolName.indexOf("Ti") >= 0) {
    resObj["toolCategory"] = "Thermal Imagers";
    resObj["toolType"] = "Handheld";
  } else if (
    toolName.indexOf("279") >= 0 ||
    toolName.indexOf("cnx") >= 0 ||
    toolName.indexOf("300") >= 0
  ) {
    resObj["toolCategory"] = "Digital Multimeters";
    resObj["toolType"] = "Handheld";
  } else if (toolName.indexOf("1587") >= 0 || toolName.indexOf("155") >= 0) {
    resObj["toolCategory"] = "Insulation Testers";
    resObj["toolType"] = "Handheld";
  } else if (toolName.indexOf("805") >= 0) {
    resObj["toolCategory"] = "Vibration meters";
    resObj["toolType"] = "Handheld";
  } else if (toolName.indexOf("173X") >= 0) {
    resObj["toolCategory"] = "PQ";
    resObj["toolType"] = "Handheld";
  } else if (toolName.indexOf("437") >= 0) {
    resObj["toolCategory"] = "Power Quality Analyzer";
    resObj["toolType"] = "Handheld";
  } else if (toolName.indexOf("12x") >= 0) {
    resObj["toolCategory"] = "Scope Meters";
    resObj["toolType"] = "Handheld";
  } else if (toolName.indexOf("Manual Entry") >= 0) {
    resObj["toolCategory"] = "Manual Entry";
    resObj["toolType"] = "Manual Entry";
  } else if (toolName.indexOf("iPhone Camera") >= 0) {
    resObj["toolName"] = "Camera";
    resObj["toolCategory"] = "Camera";
    resObj["toolType"] = "Camera";
  } else if (
    toolName.indexOf("camera") >= 0 ||
    toolName.indexOf("Camera") >= 0
  ) {
    resObj["toolCategory"] = "Camera";
    resObj["toolName"] = "Camera";
    resObj["toolType"] = "Camera";
  } else if (
    toolName.indexOf("376") >= 0 ||
    toolName.indexOf("375") >= 0 ||
    toolName.indexOf("374") >= 0 ||
    toolName.indexOf("902") >= 0
  ) {
    resObj["toolCategory"] = "Clamp Meters";
    resObj["toolType"] = "Handheld";
  } else {
    resObj["toolCategory"] = "Not Supported";
    resObj["toolType"] = "Not Supported";
  }

  return resObj;
};

const filterToolsSearchForMixpanel = (
  toolModels,
  searchCriteria,
  searchByAlternateNameFlag
) => {
  if (!toolModels || !Array.isArray(toolModels) || !searchCriteria) {
    return [];
  }
  
  var matchedTools = [];
  if (searchByAlternateNameFlag) {
    toolModels.forEach((toolInfo) => {
      if (toolInfo && toolInfo.AlternateToolName && toolInfo.AlternateToolName.indexOf(searchCriteria) >= 0) {
        matchedTools.push(toolInfo);
      }
    });
  } else {
    toolModels.forEach((toolInfo) => {
      if (toolInfo && toolInfo.ToolName && toolInfo.ToolName.indexOf(searchCriteria) >= 0) {
        matchedTools.push(toolInfo);
      }
    });
  }

  return matchedTools;
};

const getUser = (userObj) => {
  return userObj && userObj.user instanceof Array && userObj.user[0]
    ? userObj.user[0]
    : userObj && !(userObj.user instanceof Array)
    ? userObj.user
    : {};
};

// FIXED: Enhanced Mixpanel export with better error handling
let Mixpanel = {
  identify: (id) => {
    safeExecute(() => {
      if (mixpanelObj && typeof mixpanelObj.identify === 'function') {
        mixpanelObj.identify(id);
      } else {
        console.warn('Mixpanel: identify not available');
      }
    });
  },
  alias: (id) => {
    safeExecute(() => {
      if (mixpanelObj && typeof mixpanelObj.alias === 'function') {
        mixpanelObj.alias(id);
      } else {
        console.warn('Mixpanel: alias not available');
      }
    });
  },
  track: (name, props) => {
    safeExecute(() => {
      if (mixpanelObj && typeof mixpanelObj.track === 'function') {
        mixpanelObj.track(name, props);
      } else {
        console.warn('Mixpanel: track not available');
      }
    });
  },
  people: {
    set: (props) => {
      safeExecute(() => {
        if (mixpanelObj && mixpanelObj.people && typeof mixpanelObj.people.set === 'function') {
          mixpanelObj.people.set(props);
        } else {
          console.warn('Mixpanel: people.set not available');
        }
      });
    },
  },
  time_event: (name) => {
    safeExecute(() => {
      if (mixpanelObj && typeof mixpanelObj.time_event === 'function') {
        mixpanelObj.time_event(name);
      } else {
        console.warn('Mixpanel: time_event not available');
      }
    });
  },
  initiateMixpanel: initiateMixpanel,
  setMixpanelFlagsViewSession: setMixpanelFlagsViewSession,
  viewSessionDetail: viewSessionDetail,
  setupAlarms: setupAlarms,
  onClearAssignment: onClearAssignment,
  onUpdateMeasNotes: onUpdateMeasNotes,
  trackAuthenticationEvent: trackAuthenticationEvent,
  // NEW: Utility methods
  isInitialized: () => isInitialized,
  hasError: () => initializationError !== null,
  getError: () => initializationError
};

export default Mixpanel;